// Tabs
// Plain HTMLElement rather than Component: panels hold arbitrary merchant blocks, and a `ref`
// sweep would collect theirs alongside ours.
export class TabsCustom extends HTMLElement {
  #controller = new AbortController();

  /** Panel listeners are rebound on every resync, so they get their own controller. */
  #panelController = new AbortController();

  #observer = new MutationObserver(() => {
    if (this.#isStale()) this.#sync();
  });

  /** @type {string | null} */
  #activePanelId = null;

  /** @type {HTMLElement[]} */
  get tabs() {
    return Array.from(this.querySelectorAll(':scope > [role="tablist"] > [role="tab"]'));
  }

  /** @type {HTMLElement[]} */
  get panels() {
    return Array.from(this.querySelectorAll(':scope > * > [role="tabpanel"]'));
  }

  get #isRightToLeft() {
    return getComputedStyle(this).direction === 'rtl';
  }

  connectedCallback() {
    const { signal } = this.#controller;

    if (!this.tabs.length) return;

    this.#sync();

    this.addEventListener('click', this.#handleClick, { signal });
    this.addEventListener('keydown', this.#handleKeyDown, { signal });

    // The theme editor and the Section Rendering API rewrite the panels in place, which restores the
    // server markup: labels back inside their panels, every panel hidden again. Attributes matter as
    // much as children here — morph reapplies `hidden` on a node it otherwise leaves alone, so a
    // childList-only watch sees nothing when the merchant edits a setting.
    this.#observer.observe(this, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['hidden', 'aria-selected', 'tabindex'],
    });
  }

  disconnectedCallback() {
    this.#controller.abort();
    this.#panelController.abort();
    this.#observer.disconnect();
  }

  /**
   * True when the DOM no longer matches what this element set up — the shape a fresh server render
   * arrives in. Checking the result rather than the mutations keeps a resync from retriggering
   * itself.
   */
  #isStale() {
    const tabs = this.tabs;

    if (!tabs.length) return false;
    if (tabs.some((tab) => !tab.querySelector('[data-tab-label]'))) return true;

    const open = this.panels.filter((panel) => !panel.hasAttribute('hidden'));

    return open.length !== 1 || !open[0]?.hasAttribute('tabindex');
  }

  /** Claims the current markup: labels into their tabs, one panel open, panel listeners rebound. */
  #sync() {
    this.#hoistLabels();

    const tabs = this.tabs;
    let index = tabs.findIndex((tab) => tab.getAttribute('aria-controls') === this.#activePanelId);

    if (index === -1) {
      index = tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true');
    }

    this.selectTab(index === -1 ? 0 : index, { moveFocus: false });

    this.#panelController.abort();
    this.#panelController = new AbortController();

    for (const panel of this.panels) {
      panel.addEventListener('beforematch', this.#handleBeforeMatch, {
        signal: this.#panelController.signal,
      });
    }

    this.dataset.ready = '';
  }

  /**
   * Each panel ships its own label — Liquid can't read a child block's settings from the parent, so
   * the tabs render empty and collect their text here. Moving the node keeps it the accessible name
   * of the panel that points at it.
   */
  #hoistLabels() {
    for (const tab of this.tabs) {
      const panel = this.querySelector(`#${CSS.escape(tab.getAttribute('aria-controls') ?? '')}`);
      const label = panel?.querySelector(':scope > [data-tab-label]');

      // replaceChildren, not append: a re-render brings a fresh label whose id would otherwise
      // collide with the one already sitting in the tab.
      if (label) tab.replaceChildren(label);
    }
  }

  /**
   * Activates one tab and reveals its panel, hiding the rest.
   *
   * @param {number} index - Index of the tab to activate.
   * @param {object} [options]
   * @param {boolean} [options.moveFocus] - Whether to focus the newly active tab.
   */
  selectTab(index, { moveFocus = true } = {}) {
    const tabs = this.tabs;
    const panels = this.panels;
    const tab = tabs[index];

    if (!tab) return;

    this.#activePanelId = tab.getAttribute('aria-controls');

    tabs.forEach((each, position) => {
      const isActive = position === index;

      each.setAttribute('aria-selected', String(isActive));
      each.setAttribute('tabindex', isActive ? '0' : '-1');
    });

    for (const panel of panels) {
      // `until-found` keeps inactive panel text findable by find-in-page and scroll-to-text.
      // A hidden panel also loses its `tabindex`, or Tab lands on an invisible, empty stop.
      if (panel.id === tab.getAttribute('aria-controls')) {
        panel.removeAttribute('hidden');
        panel.setAttribute('tabindex', '0');
      } else {
        panel.setAttribute('hidden', 'until-found');
        panel.removeAttribute('tabindex');
      }
    }

    if (moveFocus) {
      tab.focus();
      tab.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }
  }

  /**
   * @param {MouseEvent} event
   */
  #handleClick = (event) => {
    const target = /** @type {Element} */ (event.target).closest('[role="tab"]');

    if (!target) return;

    this.selectTab(this.tabs.indexOf(/** @type {HTMLElement} */ (target)));
  };

  /**
   * Arrow keys wrap around the strip; selection follows focus, which the APG recommends when every
   * panel is already in the document.
   *
   * @param {KeyboardEvent} event
   */
  #handleKeyDown = (event) => {
    if (!(/** @type {Element} */ (event.target).closest('[role="tab"]'))) return;

    const tabs = this.tabs;
    const current = tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true');
    const step = this.#isRightToLeft ? -1 : 1;

    let next;

    switch (event.key) {
      case 'ArrowRight':
        next = current + step;
        break;
      case 'ArrowLeft':
        next = current - step;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = tabs.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    this.selectTab((next + tabs.length) % tabs.length);
  };

  /**
   * Find-in-page reveals a hidden panel on its own; sync the tabs so the strip doesn't lie.
   *
   * @param {Event} event
   */
  #handleBeforeMatch = (event) => {
    const panel = /** @type {HTMLElement} */ (event.currentTarget);

    this.selectTab(
      this.tabs.findIndex((tab) => tab.getAttribute('aria-controls') === panel.id),
      { moveFocus: false }
    );
  };

  /**
   * Activates the tab owning a given panel. Public so theme-editor.js can reveal the panel a
   * merchant selects, the way it drives the slideshows.
   *
   * @param {Element} panel - A panel belonging to this element.
   */
  selectPanel(panel) {
    if (!this.contains(panel)) return;

    this.selectTab(
      this.tabs.findIndex((tab) => tab.getAttribute('aria-controls') === panel.id),
      { moveFocus: false }
    );
  }
}

if (!customElements.get('tabs-custom')) {
  customElements.define('tabs-custom', TabsCustom);
}
